import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchRoutineDetail,
  incrementRoutineUsage,
} from "../actions/routineActions";
import { addToCart } from "../actions/cartActions";
import { useImage } from "../contexts/ImageContext";
import formatMoney from "../components/formatMoney";
import Notification from "../components/Notification";
import "../styles/routines.scss";
import { RoutineDetailSkeleton } from "../components/HomeSkeletons";

const RoutineDetail = () => {
  const { routineId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getImageUrl } = useImage();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const { currentRoutine, detailLoading, error } = useSelector(
    (state) => state.routines
  );

  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    dispatch(fetchRoutineDetail(routineId));
  }, [dispatch, routineId]);

  useEffect(() => {
    if (currentRoutine) {
      document.title = `${currentRoutine.title} | K-Shop`;
    }
  }, [currentRoutine]);

  const handleAddAllToCart = async () => {
    if (!isAuthenticated) {
      navigate(`/signin?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    if (!currentRoutine?.routine_products?.length) return;

    setAddingToCart(true);
    try {
      // Add all products to cart
      for (const rp of currentRoutine.routine_products) {
        if (rp.product?.product_id) {
          await dispatch(addToCart({ product_id: rp.product.product_id, quantity: 1 }));
        }
      }

      // Increment usage count
      dispatch(incrementRoutineUsage(currentRoutine.routine_id));

      // Save coupon code to localStorage for auto-apply at checkout
      if (currentRoutine.coupon?.coupon_code) {
        localStorage.setItem("idp_routineCoupon", currentRoutine.coupon.coupon_code);
      }

      const couponMessage = currentRoutine.coupon 
        ? ` Coupon "${currentRoutine.coupon.coupon_code}" will be auto-applied!`
        : "";
      setNotificationMessage(
        `All ${currentRoutine.routine_products.length} products added to cart!${couponMessage}`
      );
      setShowNotification(true);
    } catch (error) {
      setNotificationMessage("Failed to add some products to cart");
      setShowNotification(true);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.slug || product.product_id}`);
  };

  if (detailLoading) {
    return <RoutineDetailSkeleton />;
  }

  if (error || !currentRoutine) {
    return (
      <div className="routine-detail-page">
        <div className="routine-error">
          <h2>Routine not found</h2>
          <p>The routine you're looking for doesn't exist or has been removed.</p>
          <button onClick={() => navigate("/routines")}>
            Browse All Routines
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="routine-detail-page">
      {/* Hero Section */}
      <div
        className="routine-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.7)), url(${getImageUrl(
            currentRoutine.cover_image
          )})`,
        }}
      >
        <div className="hero-content">
          <button className="back-btn" onClick={() => navigate("/routines")}>
            ← Back to Routines
          </button>
          <div className="skin-types">
            {currentRoutine.skin_types?.map((st) => (
              <span key={st.skin_type_id} className="skin-badge">
                {st.name}
              </span>
            ))}
          </div>
          <h1>{currentRoutine.title}</h1>
          <div className="hero-meta">
            <span className="products-count">
              🧴 {currentRoutine.routine_products?.length || 0} Products
            </span>
            {currentRoutine.usage_count > 0 && (
              <span className="usage-count">
                👥 {currentRoutine.usage_count} people using this
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="routine-content-wrapper">
        {/* Description Section */}
        {currentRoutine.description && (
          <section className="routine-section description-section">
            <h2>About This Routine</h2>
            <div
              className="rich-content"
              dangerouslySetInnerHTML={{ __html: currentRoutine.description }}
            ></div>
          </section>
        )}

        {/* Coupon Banner */}
        {currentRoutine.coupon && (
          <section className="routine-section coupon-section">
            <div className="coupon-banner">
              <div className="coupon-icon">🎁</div>
              <div className="coupon-info">
                <span className="coupon-label">Special Discount Available!</span>
                <span className="coupon-value">
                  {currentRoutine.coupon.coupon_type === "percentage"
                    ? `${currentRoutine.coupon.discount}% OFF`
                    : `${formatMoney(currentRoutine.coupon.discount)} OFF`}
                </span>
                <span className="coupon-code">Code: {currentRoutine.coupon.coupon_code}</span>
              </div>
            </div>
          </section>
        )}

        {/* Products Section */}
        <section className="routine-section products-section">
          <div className="section-header">
            <h2>Products in This Routine</h2>
            <div className="total-price">
              {currentRoutine.coupon && currentRoutine.discounted_total < currentRoutine.total_price ? (
                <>
                  <span className="original-total">{formatMoney(currentRoutine.total_price || 0)}</span>
                  <strong className="discounted-total">{formatMoney(currentRoutine.discounted_total || 0)}</strong>
                </>
              ) : (
                <>
                  <span>Total: </span>
                  <strong>{formatMoney(currentRoutine.total_price || 0)}</strong>
                </>
              )}
            </div>
          </div>

          <div className="products-list">
            {currentRoutine.routine_products?.map((rp, index) => (
              <div
                key={rp.routine_product_id || index}
                className="routine-product-card"
                onClick={() => rp.product && handleProductClick(rp.product)}
              >
                <div className="product-number">{index + 1}</div>
                <div
                  className="product-image"
                  style={{
                    backgroundImage: `url(${getImageUrl(rp.product?.image)})`,
                  }}
                ></div>
                <div className="product-info">
                  <h4 className="product-brand">{rp.product?.brand}</h4>
                  <h3 className="product-name">{rp.product?.name}</h3>
                  <div className="product-price">
                    {rp.product?.discounted_price &&
                    rp.product.discounted_price !== "0.00" &&
                    rp.product.discounted_price !== rp.product.base_price ? (
                      <>
                        <span className="original-price">
                          {formatMoney(rp.product.base_price)}
                        </span>
                        <span className="current-price">
                          {formatMoney(rp.product.discounted_price)}
                        </span>
                      </>
                    ) : (
                      <span className="current-price">
                        {formatMoney(rp.product?.base_price || 0)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="product-action">
                  <span className="view-btn">View →</span>
                </div>
              </div>
            ))}
          </div>

          {/* Add All to Cart Button */}
          <div className="add-all-section">
            {currentRoutine.coupon && (
              <div className="coupon-reminder">
                🎁 Coupon "{currentRoutine.coupon.coupon_code}" will be auto-applied at checkout
              </div>
            )}
            <button
              className="add-all-btn"
              onClick={handleAddAllToCart}
              disabled={addingToCart || !currentRoutine.routine_products?.length}
            >
              {addingToCart ? (
                <>Adding...</>
              ) : (
                <>
                  Add All Products to Cart • {currentRoutine.coupon && currentRoutine.discounted_total < currentRoutine.total_price 
                    ? formatMoney(currentRoutine.discounted_total || 0) 
                    : formatMoney(currentRoutine.total_price || 0)}
                </>
              )}
            </button>
          </div>
        </section>

        {/* How to Use Section */}
        {currentRoutine.how_to_use && (
          <section className="routine-section how-to-use-section">
            <h2>How to Use</h2>
            <div
              className="rich-content how-to-use-content"
              dangerouslySetInnerHTML={{ __html: currentRoutine.how_to_use }}
            ></div>
          </section>
        )}
      </div>

      <Notification
        message={notificationMessage}
        isVisible={showNotification}
        onClose={() => setShowNotification(false)}
        actionText="View Cart"
        onActionClick={() => navigate("/cart")}
      />
    </div>
  );
};

export default RoutineDetail;

